import { SearchOutlined } from "@ant-design/icons";
import { Button, Checkbox, Empty, Input, Typography } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import type { FilterDropdownProps } from "antd/es/table/interface";
import React, { useState } from "react";

// Table column filter popup that matches CheckboxDropdown (search, checkboxes,
// Reset). Plug it in with `filterDropdown: renderTableCheckboxFilter` next to
// the column's existing `filters` and `onFilter`. Ticking a box applies the
// filter immediately, so there is no OK button.
const TableCheckboxFilter = ({
	filters,
	selectedKeys,
	setSelectedKeys,
	confirm,
	clearFilters,
}: FilterDropdownProps) => {
	const [searchTerm, setSearchTerm] = useState("");

	const filteredOptions = (filters ?? []).filter((option) =>
		String(option.text ?? option.value)
			.toLowerCase()
			.includes(searchTerm.toLowerCase())
	);

	// Toggle per checkbox rather than using Checkbox.Group's onChange, which only
	// reports the checkboxes currently rendered and would drop selections hidden
	// by the search.
	const handleCheckboxChange = (e: CheckboxChangeEvent) => {
		const value = e.target.value;
		setSelectedKeys(
			e.target.checked
				? [...selectedKeys, value]
				: selectedKeys.filter((key) => key !== value)
		);
		confirm({ closeDropdown: false });
	};

	const handleReset = () => {
		clearFilters?.({ confirm: true, closeDropdown: false });
		setSearchTerm("");
	};

	return (
		<div style={{ width: "100%" }}>
			<div className="checkbox-dropdown-content">
				<div style={{ padding: "8px", width: "auto", maxWidth: "300px" }}>
					<Input
						className="search-input"
						placeholder="Search"
						value={searchTerm}
						prefix={<SearchOutlined />}
						onChange={(e) => setSearchTerm(e.target.value)}
						onPressEnter={(e: React.KeyboardEvent<HTMLInputElement>) => {
							e.currentTarget.blur();
						}}
						allowClear
					/>
				</div>

				{filteredOptions.length === 0 ? (
					// Same empty state as antd's default filter popup
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description={filters?.length ? "No matches" : "No filters"}
						styles={{ image: { height: 24 } }}
						style={{ margin: 0, padding: "16px 0" }}
					/>
				) : (
					<Checkbox.Group
						style={{
							display: "flex",
							width: "100%",
							maxWidth: "300px",
							justifyItems: "left",
							flexDirection: "column",
							padding: "0 10px 15px",
						}}
						value={selectedKeys}
					>
						{filteredOptions.map((option) => (
							<Checkbox
								key={String(option.value)}
								value={option.value}
								className="custom-checkbox"
								title={String(option.value)}
								onChange={handleCheckboxChange}
							>
								<Typography.Paragraph
									style={{ width: 200 }}
									ellipsis={{ tooltip: option.text }}
								>
									{option.text}
								</Typography.Paragraph>
							</Checkbox>
						))}
					</Checkbox.Group>
				)}
			</div>

			<div className="checkbox-dropdown-footer">
				<Button
					type="default"
					onClick={handleReset}
					style={{
						border: "none",
						boxShadow: "none",
						background: "transparent",
					}}
				>
					Reset
				</Button>
			</div>
		</div>
	);
};

// antd calls `filterDropdown` as a plain function, not as a component, so this
// wrapper is what gives TableCheckboxFilter its own hook scope.
export const renderTableCheckboxFilter = (props: FilterDropdownProps) => (
	<TableCheckboxFilter {...props} />
);

export default TableCheckboxFilter;
